import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./MainScreenStyles.css";
import DailyWords from "./DailyWords/DailyWords";

import {
  FaApple,
  FaArrowRight,
  FaBookOpen,
  FaGooglePlay,
  FaGraduationCap,
  FaQrcode,
} from "react-icons/fa";

const IOS_STORE_URL =
  "https://apps.apple.com/de/app/learn-german-a1-c2/id6746383285?l=en-GB";

const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.kugathaj.deutsch";

const createQrUrl = (url: string): string => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=16&data=${encodeURIComponent(
    url
  )}`;
};

type LearningCard = {
  key: string;
  className: string;
  route: string;
  aria: string;
  title: string;
  subtitle: string;
  description: string;
  footer: string;
  icon: React.ReactNode;
};

type AppQrCardProps = {
  platform: string;
  device: string;
  storeUrl: string;
  variant: "ios" | "android";
  icon: React.ReactNode;
};

const learningCards: LearningCard[] = [
  {
    key: "course",
    className: "main-card-course",
    route: "/courses",
    aria: "Open German course page",
    title: "Course",
    subtitle: "ONLINE GERMAN COURSE",
    description: "Structured lessons from beginner to advanced level.",
    footer: "OPEN COURSE",
    icon: <FaGraduationCap />,
  },
  {
    key: "books",
    className: "main-card-books",
    route: "/books",
    aria: "Open German books page",
    title: "Books",
    subtitle: "60 GERMAN BOOKS",
    description: "Step-by-step books for every German learning level.",
    footer: "OPEN BOOKS",
    icon: <FaBookOpen />,
  },
];

const AppQrCard: React.FC<AppQrCardProps> = ({
  platform,
  device,
  storeUrl,
  variant,
  icon,
}) => {
  return (
    <article
      className={`main-app-qr-card main-app-qr-card-${variant}`}
      aria-label={`${platform} app download`}
    >
      <div className="main-app-qr-color-glow" aria-hidden="true" />

      <div className="main-app-qr-card-top">
        <span className={`main-app-qr-platform main-app-qr-platform-${variant}`}>
          {icon}
          {platform}
        </span>

        <span className="main-app-qr-device">{device}</span>
      </div>

      <a
        href={storeUrl}
        target="_blank"
        rel="noreferrer"
        className="main-app-qr-link"
        aria-label={`Open ${platform} app download`}
      >
        <span className="main-app-qr-frame">
          <img
            src={createQrUrl(storeUrl)}
            alt={`${platform} download QR code`}
            className="main-app-qr-image"
            loading="lazy"
          />
        </span>

        <span className="main-app-qr-overlay">
          <span>
            <FaQrcode />
            Tap or scan
          </span>
        </span>
      </a>
    </article>
  );
};

const MainScreen: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add("main-screen-page");
    document.body.classList.add("main-screen-page");

    return () => {
      document.documentElement.classList.remove("main-screen-page");
      document.body.classList.remove("main-screen-page");
    };
  }, []);

  const handleCardClick = useCallback(
    (route: string): void => {
      navigate(route);
    },
    [navigate],
  );

  return (
    <main className="main-home-container" aria-label="Main screen">
      <div className="main-screen-stack">
        <section className="main-daily-panel" aria-label="Daily word">
          <DailyWords compact />
        </section>

        <section
          className="main-bottom-grid"
          aria-label="App download and learning areas"
        >
          <div className="main-app-download-panel" aria-label="App download">
            <div className="main-panel-particles" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>

            <div className="main-app-qr-grid">
              <AppQrCard
                platform="iOS"
                device="iPhone / iPad"
                storeUrl={IOS_STORE_URL}
                variant="ios"
                icon={<FaApple />}
              />

              <AppQrCard
                platform="Android"
                device="Phones / Tablets"
                storeUrl={ANDROID_STORE_URL}
                variant="android"
                icon={<FaGooglePlay />}
              />
            </div>
          </div>

          <div className="main-learning-grid" aria-label="Learning areas">
            {learningCards.map((card) => (
              <button
                key={card.key}
                className={`main-learning-card ${card.className}`}
                type="button"
                aria-label={card.aria}
                onClick={() => handleCardClick(card.route)}
              >
                <div className="main-card-particles" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>

                <div className="main-card-badge">
                  <span className="main-badge-glow" aria-hidden="true" />

                  <div className="main-badge-icon">{card.icon}</div>

                  <div className="main-badge-content">
                    <span className="main-badge-subtitle">{card.subtitle}</span>
                    <h1 className="main-badge-title">{card.title}</h1>
                    <p className="main-badge-description">{card.description}</p>
                  </div>

                  <div className="main-badge-footer">
                    <span>{card.footer}</span>

                    <span className="main-badge-arrow">
                      <FaArrowRight />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default MainScreen;